import React, { useState } from "react";
import { 
  Typography, 
  Box, 
  Stack, 
  Grid, 
  Switch,
  FormControlLabel,
  Button,
  Card,
  CardContent
} from "@mui/material";
import { 
  TrendingUp, 
  AccountBalanceWallet,
  Payment,
  MonetizationOn
} from "@mui/icons-material";
import { motion } from "framer-motion";

import AutobiaCreditCard from "../components/Financial/AutobiaCreditCard";
import AutobiaStatsBox from "../components/DataDisplay/AutobiaStatsBox";
import ComponentDemo from "../components/ComponentDemo/ComponentDemo";

const Financial = () => {
  // Demo states
  const [cardEnabled, setCardEnabled] = useState(true);
  const [, setStatsLoading] = useState(false);

  // Demo data
  const cardNetworks = [
    { 
      id: "visa", 
      name: "VISA", 
      colors: ["#1A1F71", "#0052CC"],
      number: "•••• •••• •••• 4242"
    },
    { 
      id: "mastercard", 
      name: "MasterCard", 
      colors: ["#FF5722", "#FF9800"],
      number: "•••• •••• •••• 5555"
    },
    { 
      id: "autobia", 
      name: "Autobia Pay", 
      colors: ["#FA9940", "#4A60F4"],
      number: "•••• •••• •••• 9876"
    }
  ];

  const codeExamples = {
    creditCard: `import AutobiaCreditCard from './components/Financial/AutobiaCreditCard';

// Basic Credit Card
<AutobiaCreditCard
  cardNetwork="VISA"
  cardNumber="•••• •••• •••• 4242"
  commissionRate={0.025}
  refundRate={0.015}
  isEnabled={true}
  onEdit={() => console.log('Edit card')}
  onToggle={() => console.log('Toggle card')}
/>

// Custom Gradient Colors
<AutobiaCreditCard
  cardNetwork="Autobia Pay"
  cardNumber="•••• •••• •••• 9876"
  gradientColors={["#FA9940", "#4A60F4"]}
  commissionRate={0.03}
  refundRate={0.02}
/>`,

    statsBox: `import AutobiaStatsBox from './components/DataDisplay/AutobiaStatsBox';

// Basic Stats Box
<AutobiaStatsBox
  title="Total Revenue"
  value={245680}
  currency="SAR"
  icon={<MonetizationOn />}
  trend={12.5}
  trendDirection="up"
/>

// With Action Button
<AutobiaStatsBox
  title="Pending Cashback"
  value={15420}
  icon={<AccountBalanceWallet />}
  actionButton="Convert to Credit"
  onAction={() => console.log('Convert action')}
  variant="contained"
/>`,

    variants: `// Different Variants
<AutobiaStatsBox variant="default" />      // Default with border
<AutobiaStatsBox variant="outlined" />     // Outlined with colored border  
<AutobiaStatsBox variant="contained" />    // Filled background
<AutobiaStatsBox gradient={true} />        // Gradient background

// Different Sizes  
<AutobiaStatsBox size="small" />           // Compact size
<AutobiaStatsBox size="medium" />          // Default size
<AutobiaStatsBox size="large" />           // Large size`
  };

  return (
    <Box sx={{ padding: "2rem" }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Typography variant="h3" fontWeight={700} gutterBottom color="secondary.main">
          💳 Financial Components
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: "800px" }}>
          Specialized components for financial applications, payment processing, and money management interfaces.
          Perfect for dashboards, payment gateways, and financial analytics.
        </Typography>
      </motion.div>

      {/* Credit Card Section */}
      <ComponentDemo
        title="Credit Card Component"
        description="Beautiful animated credit card component with glassmorphism effects, gradient backgrounds, and interactive states."
        component={
          <Box>
            <Stack direction="row" spacing={3} flexWrap="wrap" gap={2}>
              {cardNetworks.map((card, index) => (
                <AutobiaCreditCard
                  key={card.id}
                  cardNetwork={card.name}
                  cardNumber={card.number}
                  commissionRate={0.025 + index * 0.005}
                  refundRate={0.015 + index * 0.005}
                  gradientColors={card.colors}
                  isEnabled={cardEnabled}
                  onEdit={() => console.log(`Edit ${card.name}`)}
                  onToggle={() => setCardEnabled(!cardEnabled)}
                  index={index}
                />
              ))}
            </Stack>
            
            <Box sx={{ mt: 3, p: 2, bgcolor: "background.default", borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Interactive Demo</Typography>
              <FormControlLabel
                control={
                  <Switch 
                    checked={cardEnabled} 
                    onChange={(e) => setCardEnabled(e.target.checked)}
                  />
                }
                label="Card Enabled"
              />
            </Box>
          </Box>
        }
        usage={codeExamples.creditCard}
      />

      {/* Enhanced Stats Box Section */}
      <ComponentDemo
        title="Enhanced Stats Box"
        description="Upgraded stats component with animations, trends, action buttons, and multiple variants for financial metrics."
        component={
          <Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={4}>
                <AutobiaStatsBox
                  title="Total Revenue"
                  subtitle="This month"
                  value={245680}
                  currency="SAR"
                  icon={<MonetizationOn fontSize="large" />}
                  trend={12.5}
                  trendDirection="up"
                  size="medium"
                  onRefresh={() => console.log('Refresh revenue')}
                />
              </Grid>
              
              <Grid item xs={12} md={6} lg={4}>
                <AutobiaStatsBox
                  title="Pending Cashback"
                  value={15420}
                  currency="SAR"
                  icon={<AccountBalanceWallet fontSize="large" />}
                  trend={-5.2}
                  trendDirection="down"
                  variant="outlined"
                  actionButton="Convert to Credit"
                  onAction={() => console.log('Convert cashback')}
                />
              </Grid>
              
              <Grid item xs={12} md={6} lg={4}>
                <AutobiaStatsBox
                  title="Payment Volume"
                  value={890145}
                  currency="SAR"
                  icon={<Payment fontSize="large" />}
                  trend={8.7}
                  trendDirection="up"
                  variant="contained"
                  gradient={true}
                  size="large"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, p: 2, bgcolor: "background.default", borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>Interactive Demo</Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setStatsLoading(true);
                    setTimeout(() => setStatsLoading(false), 2000);
                  }}
                >
                  Toggle Loading State
                </Button>
              </Stack>
            </Box>
          </Box>
        }
        usage={codeExamples.statsBox}
      />

      {/* Variants and Customization */}
      <ComponentDemo
        title="Variants and Customization"
        description="Multiple variants, sizes, and customization options for different use cases."
        component={
          <Grid container spacing={3}>
            {/* Different Variants */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>Variants</Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <AutobiaStatsBox
                title="Default"
                value={12345}
                icon={<TrendingUp />}
                variant="default"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <AutobiaStatsBox
                title="Outlined"
                value={12345}
                icon={<TrendingUp />}
                variant="outlined"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <AutobiaStatsBox
                title="Contained"
                value={12345}
                icon={<TrendingUp />}
                variant="contained"
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <AutobiaStatsBox
                title="Gradient"
                value={12345}
                icon={<TrendingUp />}
                gradient={true}
                size="small"
              />
            </Grid>
          </Grid>
        }
        usage={codeExamples.variants}
      />

      {/* Usage Guidelines */}
      <Card sx={{ mt: 4, bgcolor: "primary.main", color: "primary.contrastText" }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            💡 Usage Guidelines
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Credit Cards</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                • Use for payment method displays<br/>
                • Perfect for payment gateway interfaces<br/>
                • Great for financial dashboards<br/>
                • Include commission and refund rates
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" gutterBottom>Stats Boxes</Typography>
              <Typography variant="body2">
                • Use for KPI displays<br/>
                • Include trend indicators for context<br/>
                • Add action buttons for quick actions<br/>
                • Use different variants for hierarchy
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Financial;